import React, { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { useAuthState } from '@vis-auth/react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { EveRoutes } from '@ver-uds/e-verify';
import { AccountType } from 'eve-account-client';
import {
  useAccountEvePlusEligibilityQuery,
  useAccountQuery,
  useMouDocumentQuery,
  useTosDocumentQuery,
} from '../services/AccountApi';
import CompanyAccess from './CompanyAccess/CompanyAccess';
import CompanyEVerifyPlusBanner from './CompanyEVerifyPlusBanner';
import CompanyInformation from './CompanyInformation';
import CompanyLocations from './CompanyLocations';
import EmployerCategory from './EmployerCategory';
import HiringSites from './HiringSites';
import FullWidthLayout from '../layouts/FullWidthLayout';
import ErrorBar from './ErrorBar';
import AuthFileLink from './AuthFileLink';
import CancelTerminationSuccessAlert from './CancelTerminationSuccessAlert/CancelTerminationSuccessAlert';

const pageTitle = 'My Company Profile';

const CompanyProfile = (): JSX.Element => {
  const location = useLocation();
  const errorMessage = location.state && typeof location.state === 'string' ? String(location.state) : undefined;

  const { user } = useAuthState();
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id as string;
  const { isLoading, data: accountData } = useAccountQuery(accountId);
  const evePlusEnabled = accountData?.myI9?.enabled ?? false;
  const { isLoading: evePlusEligibilityLoading, data: isEvePlusEligible } = useAccountEvePlusEligibilityQuery(
    accountId,
    isLoading,
    evePlusEnabled,
  );
  const showEvePlusBanner = !evePlusEligibilityLoading && isEvePlusEligible;

  const linkToMou = (
    <AuthFileLink
      linkText="View Current MOU"
      query={useMouDocumentQuery}
      queryArg={accountId}
      aria-label="View Current MOU in a new tab"
    />
  );
  const linkToMyI9TOS = (
    <AuthFileLink
      linkText="View E-Verify+ Terms of Service"
      query={useTosDocumentQuery}
      queryArg={accountId}
      aria-label="View E-Verify+ Terms of Service in a new tab"
    />
  );
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - My Company Profile',
    });
  }, []);
  return (
    <FullWidthLayout title={pageTitle}>
      {showEvePlusBanner && <CompanyEVerifyPlusBanner />}
      <section className="padding-y-4">
        <CancelTerminationSuccessAlert />
        <h1>{pageTitle}</h1>
        <ErrorBar errorMessage={errorMessage} />
        {isLoading ? (
          <div className="margin-y-4">
            Loading… <img src={LoaderIcon} alt="" className="height-2" />
          </div>
        ) : (
          <>
            <CompanyInformation
              editUrl={EveRoutes.COMPANY_COMPANY_INFO_EDIT}
              companyInfoData={accountData?.companyInfo}
              accountData={accountData}
            />
            <hr />
            <EmployerCategory
              editUrl={EveRoutes.COMPANY_EMPLOYER_CATEGORY_EDIT}
              employerCategory={accountData?.companyInfo?.employerCategory}
            />
            <hr />
            <div className="grid-row grid-gap">
              <CompanyLocations accountData={accountData} editUrl={EveRoutes.COMPANY_ADDRESS_EDIT} />
              <hr className="tablet:display-none grid-col-12 margin-x-1" />
              <HiringSites />
            </div>
            <hr />
            <CompanyAccess
              accountData={accountData}
              linkToMou={linkToMou}
              linkToMyI9TOS={linkToMyI9TOS}
              showMyI9={accountData?.type === AccountType.EMPLOYER || accountData?.type === AccountType.CHILD}
            />
          </>
        )}
      </section>
    </FullWidthLayout>
  );
};

export default CompanyProfile;
